import React from 'react';
import { UserRole } from "../../../../store/store.model";
import { netTermsToString, ordinal } from "../../../../infra/utils";
import { getUnitName, pluralizeUnitName } from "../../../../utils/unit-name.utils";
import { activityLogModel, billingDayOfMonthModel, relationshipModel, serviceModel } from "../../../../models";
import { utils } from "../../../../utils";
import { PaymentMethodTypes } from "../../../../models/payment-method";
import { getAllowManualPaymentsChangeText, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeText, getInvoicesApprovalMethodChangeBullet } from "../../../../business-logic/amendmentsLogic";
import compact from 'lodash/compact';
import { AgreementsLine, EditLine, InvoicesLine, MailSentLine18, PaymentsLine } from 'seagull/icons';
import { RichTextViewer, TextButton, useGlobalContext } from 'seagull';
import { Link } from 'react-router-dom';
import { routes } from "../../../../constants";
import { getURLWithFullPageInvoice } from "../../../FullPageInvoice/FullPageInvoice.utils";
import { currencyUtils } from "../../../../utils/currency.utils";
import { EntryType } from "../../../../models/activity-log";
import { ClientPreview } from "../../../ClientPreview/ClientPreview";
export let Placeholder = /*#__PURE__*/function (Placeholder) {
  Placeholder["InvoiceLink"] = "[invoiceLink]";
  Placeholder["AgreementLink"] = "[agreementLink]";
  Placeholder["AgreementLinkUppercase"] = "[agreementLinkUppercase]";
  Placeholder["ManagePaymentDetails"] = "[managePaymentDetails]";
  Placeholder["ServiceDescription"] = "[serviceDescription]";
  Placeholder["OriginalService"] = "[originalService]";
  return Placeholder;
}({});
export function getIcon(entry) {
  switch (entry.type) {
    case EntryType.Relationship:
      {
        return activityLogModel.isReminderEntry(entry) ? /*#__PURE__*/React.createElement(MailSentLine18, null) : /*#__PURE__*/React.createElement(AgreementsLine, null);
      }
    case EntryType.Charge:
      return /*#__PURE__*/React.createElement(PaymentsLine, null);
    case EntryType.Invoice:
      return /*#__PURE__*/React.createElement(InvoicesLine, null);
    case EntryType.Service:
      return /*#__PURE__*/React.createElement(EditLine, null);
  }
}
export function getContent(config, ORIGINAL_PROPOSAL) {
  const textContent = getContentText(config, ORIGINAL_PROPOSAL);
  return replacePlaceholders(textContent, [[Placeholder.AgreementLink, () => /*#__PURE__*/React.createElement(AgreementLink, {
    key: "agreementLink",
    config: config
  })], [Placeholder.AgreementLinkUppercase, () => /*#__PURE__*/React.createElement(AgreementLink, {
    key: "agreementLinkUppercase",
    uppercase: true,
    config: config
  })], [Placeholder.ManagePaymentDetails, () => /*#__PURE__*/React.createElement(TextButton, {
    key: "managePaymentDetails",
    as: Link,
    to: routes.paymentMethods(config.relationship.id),
    "data-testid": config.testid && "".concat(config.testid, "-pm-link"),
    underline: "none"
  }, "Manage payment details")], [Placeholder.InvoiceLink, () => {
    const {
      relationship,
      testid,
      logBI
    } = config;
    const relationshipId = relationship.id;
    const entry = config.entry;
    const invoiceId = activityLogModel.invoiceId(entry);
    return /*#__PURE__*/React.createElement(TextButton, {
      key: "invoiceLink",
      as: Link,
      to: getURLWithFullPageInvoice({
        invoiceId,
        relationshipId
      }),
      onClick: () => logBI(10575, {
        invoiceId
      }),
      underline: "none",
      "data-testid": testid && "".concat(testid, "-invoice-link")
    }, /*#__PURE__*/React.createElement("span", null, activityLogModel.invoiceFormattedNumber(entry)));
  }], [Placeholder.ServiceDescription, () => {
    var _service$changes;
    const {
      entry,
      testid
    } = config;
    if (entry.type !== EntryType.Service) {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    const service = activityLogModel.service(entry);
    return /*#__PURE__*/React.createElement(RichTextViewer, {
      "data-testid": testid && "".concat(testid, "-service-description"),
      value: ((_service$changes = service.changes) === null || _service$changes === void 0 ? void 0 : _service$changes.description) || undefined
    });
  }], [Placeholder.OriginalService, () => /*#__PURE__*/React.createElement(OriginalProposalLink, {
    config: config
  })]]);
}
export function getContentText(config, ORIGINAL_PROPOSAL) {
  const entry = config.entry;
  switch (entry.type) {
    case EntryType.Relationship:
      return getAgreementContent(config, ORIGINAL_PROPOSAL);
    case EntryType.Charge:
      return getChargeContent(entry);
    case EntryType.Invoice:
      return getInvoiceContent(config);
    case EntryType.Service:
      return getServiceContent(entry);
  }
}
function getAgreementContent(config, ORIGINAL_PROPOSAL) {
  const {
    entry,
    isVendor
  } = config;
  if (!activityLogModel.isAgreementEntry(entry)) {
    return null;
  }
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customer = activityLogModel.customer(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const paymentMethod = activityLogModel.agreementPaymentMethod(entry);
  const channel = activityLogModel.agreementChannel(entry);
  switch (activityLogModel.agreementState(entry)) {
    case activityLogModel.AgreementState.Sent:
      {
        const reviewers = entry.data.reviewers;
        const reviewersText = reviewers ? " and the following additional recipients: ".concat(reviewers.map(activityLogModel.reviewerNameAndEmail).join(', ')) : '';
        return "".concat(Placeholder.AgreementLinkUppercase, " sent to ").concat(activityLogModel.customerNameAndEmail(entry)).concat(reviewersText, ". ").concat(ORIGINAL_PROPOSAL && isVendor ? Placeholder.OriginalService : '');
      }
    case activityLogModel.AgreementState.Reviewed:
      return "".concat(Placeholder.AgreementLinkUppercase, " was reviewed by ").concat(customerName, ".");
    case activityLogModel.AgreementState.MicroDepositStarted:
    case activityLogModel.AgreementState.MicroDeposit:
      return "".concat(customerName, " initiated a micro-deposit verification.");
    case activityLogModel.AgreementState.MicroDepositStartedV2:
      return "Micro-deposit verification process for bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") has been initiated.");
    case activityLogModel.AgreementState.MicroDepositFailed:
      return "Micro-deposit verification failed.";
    case activityLogModel.AgreementState.MicroDepositFailedV2:
      return "Bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") failed verification. ").concat(Placeholder.ManagePaymentDetails, ".");
    case activityLogModel.AgreementState.MicroDepositVerified:
      return "Micro-deposit was verified by ".concat(customerName || vendorName, ".");
    case activityLogModel.AgreementState.MicroDepositVerifiedV2:
      return "Bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") was verified and connected successfully.");
    case activityLogModel.AgreementState.PaymentMethod:
      return "".concat(customerName || vendorName, " added a payment method.");
    case activityLogModel.AgreementState.Approved:
      return "".concat(Placeholder.AgreementLinkUppercase, " approved by ").concat(customerName, ".");
    case activityLogModel.AgreementState.Terminated:
      return "".concat(Placeholder.AgreementLinkUppercase, " terminated by ").concat(vendorName, ".");
    case activityLogModel.AgreementState.Changed:
      {
        const netTerms = activityLogModel.agreementChangesNetTerms(entry);
        const netTermsText = netTerms != null ? "Payment terms: ".concat(netTermsToString(netTerms)) : '';
        return "".concat(Placeholder.AgreementLinkUppercase, " was edited by ").concat(vendorName, " and now includes the following:\n").concat(netTermsText);
      }
    case activityLogModel.AgreementState.ChangesApproved:
      return "The latest amendments have been approved by ".concat(customerName, ".");
    case activityLogModel.AgreementState.ChangesAutoApproved:
      return "As per the agreement terms, the latest amendments are effective as of today.";
    case activityLogModel.AgreementState.StoppedAutoApproval:
      return "".concat(customerName, " declined the auto-approval of the latest amendments.");
    case activityLogModel.AgreementState.SigningReminderSent:
      return "Anchor sent an automatic ".concat(channel, " reminder to ").concat(customerName, ", to approve the proposal.");
    case activityLogModel.AgreementState.SigningReminderSentManual:
      return "".concat(vendorName, " sent an ").concat(channel, " reminder to ").concat(customerName, ", to approve the proposal.");
    case activityLogModel.AgreementState.MicroDepositReminderSent:
      return "Anchor sent an automatic micro deposit verification reminder to ".concat(customerName, ".");
    case activityLogModel.AgreementState.PaymentMethodChanged:
      {
        if (!paymentMethod || !customer) return null;
        const pmText = paymentMethod.type === PaymentMethodTypes.CreditCard ? 'Credit card' : 'Bank account';
        return "The ".concat(Placeholder.AgreementLink, " has been updated with a new payment method - ").concat(pmText, " ending in ").concat(paymentMethod.last4, " by ").concat(customer.name).concat(customer.role === UserRole.Accountant ? " (".concat(customer.email, ")") : '', ".");
      }
    case activityLogModel.AgreementState.SignedExternally:
      return "Existing agreement was uploaded and authorized for billing on Anchor by ".concat(vendorName, ".");
    case activityLogModel.AgreementState.RequestedPaymentMethod:
      {
        if (!customer) return null;
        return "A request to add a payment method has been sent to ".concat(customer.name, " (").concat(customer.email, ")");
      }
    case activityLogModel.AgreementState.LegalTermsUpdated:
      return "Terms & Conditions have been updated.";
    case activityLogModel.AgreementState.AmendmentsApprovalMethodUpdated:
      {
        const approvalMethodChanges = activityLogModel.agreementChangesAmendmentsApprovalMethod(entry);
        if (!approvalMethodChanges) return null;
        const bullet = getAmendmentsApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
        return bullet ? "".concat(bullet.text).concat(vendorName ? " by ".concat(vendorName) : '', ".") : null;
      }
    case activityLogModel.AgreementState.InvoicesApprovalMethodUpdated:
      {
        const approvalMethodChanges = activityLogModel.agreementChangesInvoicesApprovalMethod(entry);
        if (!approvalMethodChanges) return null;
        const bullet = getInvoicesApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
        return bullet ? "".concat(bullet.text).concat(vendorName ? " by ".concat(vendorName) : '', ".") : null;
      }
    case activityLogModel.AgreementState.TitleUpdated:
      {
        const changesTitle = activityLogModel.agreementChangesTitle(entry);
        return changesTitle ? "".concat(vendorName, " renamed this ").concat(Placeholder.AgreementLink, " \"").concat(changesTitle.after, "\"") : null;
      }
    case activityLogModel.AgreementState.ManualPaymentsUpdated:
      {
        const changesManualPayments = activityLogModel.agreementChangesManualPayments(entry);
        if (!changesManualPayments) return null;
        const {
          before,
          after
        } = changesManualPayments;
        return "".concat(getAllowManualPaymentsChangeText(before, after), " by ").concat(vendorName, ".");
      }
    case activityLogModel.AgreementState.CoverCreditCardFeesUpdated:
      {
        const changesCoverCCFees = activityLogModel.agreementChangesCoverCCFees(entry);
        if (!changesCoverCCFees) return null;
        const changeText = getCoverCCFeesChangeText(changesCoverCCFees.before, changesCoverCCFees.after, relationshipModel.vendorName(config.relationship), relationshipModel.customerName(config.relationship));
        return "".concat(changeText, " by ").concat(vendorName);
      }
    case activityLogModel.AgreementState.SellerUpdated:
      {
        const changesSeller = activityLogModel.agreementChangesSeller(entry);
        if (!changesSeller) return null;
        return "Assignee changed from ".concat(changesSeller.before, " to ").concat(changesSeller.after, " by ").concat(vendorName, ".");
      }
    default:
      return null;
  }
}
function getChargeContent(entry) {
  const charge = activityLogModel.charge(entry);
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const chargeNote = activityLogModel.chargeNote(entry);
  const formattedPrice = activityLogModel.chargePrice(entry).display();
  switch (activityLogModel.chargeState(entry)) {
    case activityLogModel.ChargeState.Created:
      {
        const noteText = chargeNote ? ", with the following note: ".concat(chargeNote) : '';
        return "A new charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, " was added by ").concat(vendorName).concat(noteText, ".");
      }
    case activityLogModel.ChargeState.Approved:
      return "A charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, ", was approved by ").concat(customerName, ".");
    case activityLogModel.ChargeState.Canceled:
      return "The charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, ", was canceled by ").concat(vendorName, ".");
    default:
      return null;
  }
}
function getInvoiceContent(config) {
  var _entry$data$paymentSt, _entry$data$refundReq;
  const {
    relationship
  } = config;
  const entry = config.entry;
  if (entry.type !== EntryType.Invoice) {
    return null;
  }
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const customerBusinessName = relationshipModel.customerName(relationship);
  const totalAmount = activityLogModel.invoiceTotalAmount(entry);
  const totalAmountDisplay = totalAmount.display();
  switch (activityLogModel.invoiceState(entry)) {
    case activityLogModel.InvoiceState.Voided:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was voided by ").concat(vendorName, ".");
    case activityLogModel.InvoiceState.PaymentStoppedOLD:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was disputed by ").concat(customerName, ".");
    case activityLogModel.InvoiceState.PaymentStopped:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " was stopped by ").concat(customerName, " from ").concat(customerBusinessName, " with the following note: \"").concat(((_entry$data$paymentSt = entry.data.paymentStopped) === null || _entry$data$paymentSt === void 0 ? void 0 : _entry$data$paymentSt.reason) || '', "\".");
    case activityLogModel.InvoiceState.PaymentResumed:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " was resumed by ").concat(customerName, " from ").concat(customerBusinessName, ".");
    case activityLogModel.InvoiceState.Completed:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was finalized (added hours/units) by ").concat(vendorName, ".");
    case activityLogModel.InvoiceState.IssuedIncomplete:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was issued but has missing quantity/hours.");
    case activityLogModel.InvoiceState.Issued:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was issued.");
    case activityLogModel.InvoiceState.PaymentFailed:
      return "Invoice ".concat(Placeholder.InvoiceLink, ": Payment failed.");
    case activityLogModel.InvoiceState.PaymentRetried:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was charged manually.");
    case activityLogModel.InvoiceState.Paid:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was paid.");
    case activityLogModel.InvoiceState.PaidExternally:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was marked as paid.");
    case activityLogModel.InvoiceState.DisputeResolved:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " has been stopped by ").concat(customerName, " from ").concat(customerBusinessName, ".");
    case activityLogModel.InvoiceState.Approved:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was approved by ").concat(customerName, ".");
    case activityLogModel.InvoiceState.PaymentAutoApproved:
      return "As per the agreement terms, invoice ".concat(Placeholder.InvoiceLink, " has been auto-approved.");
    case activityLogModel.InvoiceState.AutoApprovalDisabled:
      return "The auto approval of invoice ".concat(Placeholder.InvoiceLink, " has been disabled by ").concat(customerName, ".");
    case activityLogModel.InvoiceState.Resent:
      return "Invoice ".concat(Placeholder.InvoiceLink, " resent by ").concat(vendorName, ".");
    case activityLogModel.InvoiceState.PaymentConfirmed:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was confirmed.");
    case activityLogModel.InvoiceState.PaymentProcessing:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was processed.");
    case activityLogModel.InvoiceState.Refunded:
      return "Invoice ".concat(Placeholder.InvoiceLink, " for ").concat(totalAmountDisplay, " has been refunded.");
    case activityLogModel.InvoiceState.PaymentDisputed:
      return "Invoice ".concat(Placeholder.InvoiceLink, ": Payment disputed.");
    case activityLogModel.InvoiceState.PayNowRequested:
      return "Invoice ".concat(Placeholder.InvoiceLink, " has been requested to be paid now by ").concat(customerName, ".");
    case activityLogModel.InvoiceState.RefundRequested:
      return "".concat(vendorName, " requested to refund invoice ").concat(Placeholder.InvoiceLink, " to the agreement with the following note: \"").concat(((_entry$data$refundReq = entry.data.refundRequested) === null || _entry$data$refundReq === void 0 ? void 0 : _entry$data$refundReq.reason) || '', "\".");
    default:
      return null;
  }
}
function getServiceContent(entry) {
  const state = activityLogModel.serviceState(entry);
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const service = activityLogModel.service(entry);
  switch (state) {
    case activityLogModel.ServiceState.Approved:
      return "The addition of ".concat(service.name, " service was approved by ").concat(customerName, ".");
    case activityLogModel.ServiceState.ChangesApproved:
      return "An amendment to ".concat(service.name, " service was approved by ").concat(customerName, ".");
    case activityLogModel.ServiceState.ChangesAutoApproved:
      return "As per the agreement terms, the amendments to ".concat(service.name, " service are effective as of today.");
    case activityLogModel.ServiceState.Resumed:
      return "".concat(vendorName, " resumed the ").concat(service.name, " service.");
    case activityLogModel.ServiceState.Paused:
      return "".concat(vendorName, " paused the ").concat(service.name, " service.");
    case activityLogModel.ServiceState.Created:
      {
        return compact(["A new service was added by ".concat(vendorName, "."), "The ".concat(service.name, " service was added and includes the following:"), getServiceDescription(service), getServicePricePerUnitText(service), getServiceDiscountText(service), getServiceCap(service), getServiceRecurrenceText(service), getServiceNote(service)]).join('\n');
      }
    case activityLogModel.ServiceState.Changed:
      {
        return compact(["An existing service was edited by ".concat(vendorName, "."), "The ".concat(service.name, " service was edited and now includes the following:"), getServiceDescription(service), getServicePricePerUnitText(service), getServiceDiscountText(service), getServiceCap(service), getServiceRecurrenceText(service), getServiceNote(service)]).join('\n');
      }
    case activityLogModel.ServiceState.Terminated:
      return "Service ".concat(service.name, " has been stopped by ").concat(vendorName);
    case activityLogModel.ServiceState.Canceled:
      return "Service ".concat(service.name, " has been canceled by ").concat(vendorName);
    case activityLogModel.ServiceState.BillCommandDeliverablesPendingApproval:
      return "Delivery of ".concat(service.name, " was sent by ").concat(vendorName, ".");
    case activityLogModel.ServiceState.BillCommandDeliverablesApproved:
      return "Delivery of ".concat(service.name, " was approved by ").concat(customerName, ".");
    case activityLogModel.ServiceState.BillCommandDeliverablesRejected:
      return "Delivery of ".concat(service.name, " was rejected by ").concat(customerName, ".");
    case activityLogModel.ServiceState.BillCommandDeliverablesWithdrawn:
      return "Delivery of ".concat(service.name, " was withdrawn by ").concat(vendorName, ".");
    case activityLogModel.ServiceState.ChangesDiscarded:
      return "Amendment of ".concat(service.name, " was discarded by ").concat(vendorName, ".");
    case activityLogModel.ServiceState.PriceIncreased:
      {
        const changesPrice = activityLogModel.changesPrice(service);
        const beforeChangesPrice = activityLogModel.beforeChangesPrice(service);
        return changesPrice && beforeChangesPrice ? "As per the agreement terms, the price of \u201C".concat(service.name, "\u201D automatically increased from ").concat(currencyUtils.formatDollarAmount(beforeChangesPrice), " to ").concat(currencyUtils.formatDollarAmount(changesPrice), ".") : null;
      }
    case activityLogModel.ServiceState.IntegratedClassChanged:
      {
        const before = activityLogModel.beforeChangesIntegratedClassName(service) || '';
        const after = activityLogModel.changesIntegratedClassName(service) || '';
        return "The class of service ".concat(service.name, " has been changed from ").concat(before, " to ").concat(after, " by ").concat(vendorName, ".");
      }
    default:
      return null;
  }
}
function replacePlaceholders(content, replacers) {
  if (!content) {
    return null;
  }
  let result = [content];
  replacers.forEach(replacer => {
    result = replacePlaceholder(result, replacer);
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, result);
}
function replacePlaceholder(content, _ref) {
  let [placeholder, getReplacedContent] = _ref;
  return content.map(item => {
    if (typeof item !== 'string') {
      return item;
    }
    const splitted = item.split(placeholder);
    if (splitted.length === 1) {
      return item;
    }
    const result = [];
    splitted.forEach((item, index) => {
      result.push(item);
      if (index < splitted.length - 1) {
        result.push(getReplacedContent());
      }
    });
    return compact(result);
  }).flat(1);
}
function getServiceDescription(service) {
  var _service$changes2;
  return (_service$changes2 = service.changes) !== null && _service$changes2 !== void 0 && _service$changes2.description ? "Description:\n".concat(Placeholder.ServiceDescription) : '';
}
function getServiceNote(service) {
  return service.note ? "Note: ".concat(service.note) : '';
}
function getServiceRecurrenceText(service) {
  if (!service.changes) {
    return '';
  }
  const {
    billing
  } = service.changes;
  const isRecurring = !!billing && serviceModel.isRecurring(billing);
  const recurrenceText = isRecurring ? " with ".concat(serviceModel.recurrencePeriod(billing), " billing") : '';
  const dayOfWeek = billing ? serviceModel.billingDayOfWeek({
    billing
  }) : null;
  const dayOfMonth = billing ? serviceModel.billingDayOfMonth({
    billing
  }) : null;
  const dayText = dayOfWeek ? " (on ".concat(dayOfWeek, ")") : dayOfMonth ? " (on the ".concat(billingDayOfMonthModel.isLastDayOfMonth(dayOfMonth) ? 'last day' : ordinal(dayOfMonth.day), " of each month)") : '';
  return "The service will be ".concat(isRecurring ? 'an ongoing' : 'a one-time', " service").concat(recurrenceText).concat(dayText, ".");
}
function getServicePricePerUnitText(service) {
  const {
    cost
  } = service.changes || {};
  const price = cost && serviceModel.price({
    cost
  });
  if (price) {
    const formattedPrice = utils.formatDollarAmount(price);
    if (serviceModel.isVariablePrice(cost)) {
      return "Price: ".concat(formattedPrice, "/").concat(getUnitName(serviceModel.unitName({
        cost
      })));
    } else if (serviceModel.isRangePrice(cost)) {
      const minPrice = serviceModel.minPrice({
        cost
      });
      return "Price: ".concat(minPrice ? utils.formatDollarAmount(minPrice) : '0', "-").concat(formattedPrice, " range price");
    } else {
      return "Price: ".concat(formattedPrice, " fixed price");
    }
  } else {
    return '';
  }
}
function getServiceDiscountText(service) {
  const {
    cost
  } = service.changes || {};
  const discount = cost && serviceModel.discount({
    cost
  });
  return discount ? "Discount of ".concat(utils.formatDiscount(discount)) : '';
}
function getServiceCap(service) {
  const {
    cost
  } = service.changes || {};
  const unitCap = cost && serviceModel.unitCap({
    cost
  });
  return unitCap ? "Preapproved: ".concat(utils.formatNumberWithCommas(unitCap), " ").concat(pluralizeUnitName(unitCap, serviceModel.unitName({
    cost
  }))) : '';
}
function AgreementLink(props) {
  const {
    uppercase,
    config: {
      relationship,
      logBI,
      testid
    }
  } = props;
  return /*#__PURE__*/React.createElement(TextButton, {
    as: Link,
    to: routes.agreement(relationship.id),
    onClick: () => logBI(10576),
    "data-testid": testid && "".concat(testid, "-agreement-link"),
    underline: "none"
  }, uppercase ? 'Agreement' : 'agreement');
}
const OriginalProposalLink = props => {
  const {
    config: {
      relationship,
      testid
    }
  } = props;
  const globalContext = useGlobalContext();
  return /*#__PURE__*/React.createElement(TextButton, {
    onClick: () => {
      globalContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref2 => {
          let {
            dismissModal,
            registerCanDismiss
          } = _ref2;
          return /*#__PURE__*/React.createElement(ClientPreview, {
            type: "originalProposal",
            relationshipId: relationship.id,
            dismissModal: dismissModal,
            registerCanDismiss: registerCanDismiss
          });
        }
      });
    },
    "data-testid": testid && "".concat(testid, "-view-original-proposal"),
    underline: "none"
  }, 'View original proposal');
};